import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import type { PermissionKey } from "@laundrypro/shared";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { BrandingProvider, useBranding } from "./context/BrandingContext";
import { ToastProvider, BrandLoader } from "./components/ui";
import { AppShell } from "./layouts/AppShell";
import { LoginPage } from "./pages/LoginPage";
import { SetupPage } from "./pages/SetupPage";
import { DashboardPage } from "./pages/DashboardPage";
import { ChangePasswordPage } from "./pages/ChangePasswordPage";
import { ModulePlaceholder } from "./pages/ModulePlaceholder";
import { CustomersPage } from "./pages/CustomersPage";
import { CustomerDetailPage } from "./pages/CustomerDetailPage";
import { OrdersPage } from "./pages/OrdersPage";
import { OrderCreatePage } from "./pages/OrderCreatePage";
import { OrderDetailPage } from "./pages/OrderDetailPage";
import { InvoicesPage } from "./pages/InvoicesPage";
import { InvoiceDetailPage } from "./pages/InvoiceDetailPage";
import { UsersPage } from "./pages/UsersPage";
import { RolesPage } from "./pages/RolesPage";
import { SettingsPage } from "./pages/SettingsPage";
import { SettingsHubPage } from "./pages/SettingsHubPage";
import { BrandingPage } from "./pages/BrandingPage";
import { TaxSettingsPage } from "./pages/TaxSettingsPage";
import { AuditLogsPage } from "./pages/AuditLogsPage";

/**
 * Route guard.
 *
 * Redirecting away from a route the user cannot use is a courtesy — it avoids a
 * screen that would only fill with 403s. It is NOT the control: every request
 * the page would have made is refused by the Worker independently.
 */
function Protected({ permission, children }: { permission?: PermissionKey; children: React.ReactNode }) {
  const { user, loading, hasPermission, mustChangePassword } = useAuth();
  const { language } = useBranding();

  if (loading) return <BrandLoader label={language === "ar" ? "جارٍ التحميل…" : "Loading…"} />;
  if (mustChangePassword) return <Navigate to="/change-password" replace />;
  if (!user) return <Navigate to="/login" replace />;
  if (permission && !hasPermission(permission)) return <Navigate to="/dashboard" replace />;

  return <>{children}</>;
}

function RootRedirect() {
  const { user, loading, mustChangePassword } = useAuth();
  const { branding, loading: brandingLoading } = useBranding();

  if (loading || brandingLoading) return <BrandLoader />;
  if (branding.setupRequired) return <Navigate to="/setup" replace />;
  if (mustChangePassword) return <Navigate to="/change-password" replace />;
  return <Navigate to={user ? "/dashboard" : "/login"} replace />;
}

/**
 * Modules whose permissions exist and are enforced, but whose interface belongs
 * to a later phase. They are routed so navigation is honest about what exists —
 * a nav item that leads nowhere is worse than one that says "not built yet".
 */
const PLACEHOLDER_ROUTES: ReadonlyArray<{
  path: string;
  key: string;
  permission: PermissionKey;
  phaseAr: string;
  phaseEn: string;
}> = [
  { path: "/services", key: "services", permission: "services.view", phaseAr: "المرحلة ٢ — نطاق المغسلة", phaseEn: "Phase 2 — Laundry domain" },
  { path: "/pricing", key: "pricing", permission: "pricing.view", phaseAr: "المرحلة ٢ — نطاق المغسلة", phaseEn: "Phase 2 — Laundry domain" },
  { path: "/payments", key: "payments", permission: "payments.view", phaseAr: "المرحلة ٣ — المدفوعات", phaseEn: "Phase 3 — Payments" },
  { path: "/expenses", key: "expenses", permission: "expenses.view", phaseAr: "المرحلة ٨ — المخزون والمصروفات", phaseEn: "Phase 8 — Inventory and expenses" },
  { path: "/inventory", key: "inventory", permission: "inventory.view", phaseAr: "المرحلة ٨ — المخزون والمصروفات", phaseEn: "Phase 8 — Inventory and expenses" },
  { path: "/reports", key: "reports", permission: "reports.view", phaseAr: "المرحلة ٧ — التقارير", phaseEn: "Phase 7 — Reports" },
];

export function App() {
  return (
    <BrowserRouter>
      <BrandingProvider>
        <ToastProvider>
          <AuthProvider>
            <Routes>
              <Route path="/" element={<RootRedirect />} />
              <Route path="/setup" element={<SetupPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/change-password" element={<ChangePasswordPage />} />

              <Route
                element={
                  <Protected>
                    <AppShell />
                  </Protected>
                }
              >
                <Route path="/dashboard" element={<DashboardPage />} />

                <Route
                  path="/customers"
                  element={
                    <Protected permission="customers.view">
                      <CustomersPage />
                    </Protected>
                  }
                />
                <Route
                  path="/customers/:id"
                  element={
                    <Protected permission="customers.view">
                      <CustomerDetailPage />
                    </Protected>
                  }
                />
                <Route
                  path="/orders"
                  element={
                    <Protected permission="orders.view">
                      <OrdersPage />
                    </Protected>
                  }
                />
                <Route
                  path="/orders/new"
                  element={
                    <Protected permission="orders.create">
                      <OrderCreatePage />
                    </Protected>
                  }
                />
                <Route
                  path="/orders/:id"
                  element={
                    <Protected permission="orders.view">
                      <OrderDetailPage />
                    </Protected>
                  }
                />
                <Route
                  path="/invoices"
                  element={
                    <Protected permission="invoices.view">
                      <InvoicesPage />
                    </Protected>
                  }
                />
                {/*
                  Print settings live in ONE place: Settings → Printing and
                  invoices. This route used to render a second, standalone
                  editor writing the same `invoice_settings` columns — which is
                  how a "both" receipt-language option survived here long after
                  no template could render it. Two editors for one row is a
                  drift generator, so the old URL redirects instead of being a
                  broken link for anyone who bookmarked it.
                */}
                <Route path="/invoices/settings" element={<Navigate to="/settings" replace />} />
                <Route
                  path="/invoices/:id"
                  element={
                    <Protected permission="invoices.view">
                      <InvoiceDetailPage />
                    </Protected>
                  }
                />
                <Route
                  path="/users"
                  element={
                    <Protected permission="users.view">
                      <UsersPage />
                    </Protected>
                  }
                />
                <Route
                  path="/roles"
                  element={
                    <Protected permission="roles.view">
                      <RolesPage />
                    </Protected>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <Protected permission="settings.view">
                      <SettingsHubPage />
                    </Protected>
                  }
                />
                <Route
                  path="/branding"
                  element={
                    <Protected permission="branding.view">
                      <BrandingPage />
                    </Protected>
                  }
                />
                <Route
                  path="/tax-settings"
                  element={
                    <Protected permission="tax_settings.view">
                      <TaxSettingsPage />
                    </Protected>
                  }
                />
                <Route
                  path="/audit-logs"
                  element={
                    <Protected permission="audit_logs.view">
                      <AuditLogsPage />
                    </Protected>
                  }
                />
                {PLACEHOLDER_ROUTES.map((route) => (
                  <Route
                    key={route.path}
                    path={route.path}
                    element={
                      <Protected permission={route.permission}>
                        <ModulePlaceholder
                          moduleKey={route.key}
                          phaseAr={route.phaseAr}
                          phaseEn={route.phaseEn}
                        />
                      </Protected>
                    }
                  />
                ))}
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AuthProvider>
        </ToastProvider>
      </BrandingProvider>
    </BrowserRouter>
  );
}
